import { useState } from 'react';
import { formatVND, formatNumber } from '../../../../utils/format';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { LOW_YIELD_RATIO } from '../../../../utils/status';
import type { Harvest } from '../../../../types';

interface HarvestConfirmModalProps {
  harvest: Harvest;
  onClose: () => void;
  onConfirm: (confirmedYield: number, qualityNote: string) => void;
}

export default function HarvestConfirmModal({
  harvest, onClose, onConfirm,
}: HarvestConfirmModalProps) {
  useModalBackHandler(true, onClose);
  const [confirmedYield, setConfirmedYield] = useState(
    String(harvest.actualYield || '')
  );
  const [qualityNote, setQualityNote] = useState('');

  const yieldNum = Number(confirmedYield) || 0;
  // This harvest's own expected quantity — same record, same `donVi`. Using the
  // season's expectedYield here compared two different units (see HarvestCard).
  const duKien = harvest.soLuong || 0;
  const chenhLech = yieldNum - duKien;
  // Preview the same threshold the server will actually apply — hardcoded 0.8
  // here promised a "Sản lượng thấp" flag for confirmations between 70% and 80%
  // that never got one.
  const isLowYield = duKien > 0 && yieldNum < duKien * LOW_YIELD_RATIO;

  const handleConfirm = () => {
    if (!confirmedYield.trim() || yieldNum <= 0) {
      showToast('Vui lòng nhập sản lượng xác nhận', 'warning');
      return;
    }
    onConfirm(yieldNum, qualityNote);
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md overflow-y-auto max-h-[100dvh] sm:max-h-[90vh]">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">Xác nhận thu hoạch</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4">
          {/* Sản lượng nông hộ */}
          <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-2">
            <p className="text-sm font-medium text-slate-700">Thông tin nông hộ cập nhật</p>
            <p className="text-sm text-slate-600">
              Sản lượng nông hộ báo: <span className="font-medium text-slate-900">{harvest.actualYield ? `${formatNumber(harvest.actualYield)} ${harvest.donVi}` : 'Chưa có'}</span>
            </p>
            <p className="text-sm text-slate-600">
              Sản lượng dự kiến: <span className="font-medium text-slate-900">{formatNumber(duKien)} {harvest.donVi}</span>
            </p>
            {harvest.chatLuong && (
              <p className="text-sm text-slate-600">Chất lượng nông hộ: <span className="font-medium text-slate-900">{harvest.chatLuong}</span></p>
            )}
          </div>

          {/* Nhập sản lượng HTX */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sản lượng xác nhận ({harvest.donVi}) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={confirmedYield}
                onChange={(e) => setConfirmedYield(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder={`Ví dụ: ${harvest.actualYield || harvest.soLuong}`}
              />
            </div>

            {/* Preview chênh lệch */}
            {confirmedYield && yieldNum > 0 && (
              <div className={`p-3 rounded-xl border text-sm ${isLowYield ? 'bg-red-50 border-red-200 text-red-700' : chenhLech >= 0 ? 'bg-agrida-50 border-agrida-200 text-agrida-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${isLowYield ? 'ri-error-warning-line' : chenhLech >= 0 ? 'ri-check-line' : 'ri-information-line'}`} />
                  </span>
                  <p className="font-medium">
                    Chênh lệch: {chenhLech >= 0 ? '+' : ''}{formatNumber(chenhLech)} {harvest.donVi}
                  </p>
                </div>
                {duKien > 0 && (
                  <p className="mt-1 text-xs ml-7">
                    {((yieldNum / duKien) * 100).toFixed(1)}% so với dự kiến
                    {isLowYield && ` — Sản lượng thấp hơn ${Math.round(LOW_YIELD_RATIO * 100)}% dự kiến, sẽ đánh dấu "Sản lượng thấp"`}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú chất lượng</label>
              <textarea
                value={qualityNote}
                onChange={(e) => setQualityNote(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="Nhập nhận xét chất lượng, vấn đề phát hiện..."
              />
              <p className="text-xs text-slate-400 mt-1">{qualityNote.length}/500</p>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap min-h-[44px]"
            >
              Xác nhận thu hoạch
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
